import axios from 'axios'
import { chunk } from 'lodash'
import { PDFDocument } from 'pdf-lib'

import { axiosInstance } from '../hooks/useAxios'
import {
  IFlashTrackingLabel,
  IFlashTrackingLabelsResponse,
} from '../types/flash'

const FLASH_LABELS_CHUNK_SIZE = 200

export class PdfUtil {
  provider: string
  setPopupMessage: ((message: string) => void) | undefined
  successOrderIds: string[] = []

  constructor(provider: string) {
    this.provider = provider
  }

  public setPopupMessageHandler(handler: (message: string) => void) {
    this.setPopupMessage = handler
  }

  private clearSuccessOrderIds() {
    this.successOrderIds = []
  }

  public getSuccessOrderIds(): string[] {
    return this.successOrderIds
  }

  public setSuccessOrderIds(orderIds: string[]) {
    this.successOrderIds = orderIds
  }

  public async openLink(data: Record<string, any>): Promise<void> {
    try {
      const response = await axiosInstance.post(
        `/trackings/${this.provider}/print`,
        data,
      )
      const url = response?.data?.url ?? null
      if (url) {
        window.open(url, '_blank')
        return
      }
      alert(`ไม่พบรายการพิมพ์ในระบบ`)
    } catch (error) {
      console.error(error)
    }
  }

  public async thaipost(data: Record<string, any>): Promise<any> {}

  private async fetchFlashPdf(
    orderId: string,
    size: string | number,
  ): Promise<ArrayBuffer> {
    const response = await axiosInstance.post(
      `/trackings/${this.provider}/print`,
      {
        provider: this.provider,
        orders: [orderId],
        note: '',
        size: size,
      },
      {
        responseType: 'arraybuffer',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/pdf',
        },
      },
    )
    return response.data
  }

  public async flash(orderIds: string[], size: string | number): Promise<any> {
    const mergedPdf = await PDFDocument.create()
    this.clearSuccessOrderIds()
    let success = 0
    for (const orderId of orderIds) {
      try {
        const pdfBytes = await this.fetchFlashPdf(orderId, size)
        const pdf = await PDFDocument.load(pdfBytes)
        const copiedPages = await mergedPdf.copyPages(pdf, pdf.getPageIndices())
        copiedPages.forEach((page: any) => {
          mergedPdf.addPage(page)
        })
        success++
        this.setPopupMessage?.('กำลังพิมพ์ ' + success + '/' + orderIds.length)
        this.successOrderIds.push(orderId)
      } catch (e) {
        console.error(e)
      }
    }

    if (success > 0) {
      const mergedPdfFile = await mergedPdf.save()
      const blob = new Blob([mergedPdfFile], { type: 'application/pdf' })
      const url = window.URL.createObjectURL(blob)
      window.open(url, '_blank')
    } else {
      alert(`ไม่พบรายการพิมพ์ในระบบ`)
    }
  }

  public async flashSelfLabel(
    orderIds: string[],
    size: string | number,
    renderLabels: (
      orderIds: string[],
      labels: Record<string, IFlashTrackingLabel>,
    ) => Promise<Blob>,
  ): Promise<{ fallback: number; failed: number }> {
    this.clearSuccessOrderIds()
    this.setPopupMessage?.('กำลังดึงข้อมูลใบปะหน้า')
    const labels: Record<string, IFlashTrackingLabel> = {}
    const missing = new Set<string>()
    for (const ids of chunk(orderIds, FLASH_LABELS_CHUNK_SIZE)) {
      const response = await axiosInstance.post<IFlashTrackingLabelsResponse>(
        `/trackings/${this.provider}/labels`,
        { orders: ids },
      )
      response.data.labels.forEach((label) => {
        labels[label.order] = label
      })
      response.data.missing.forEach((orderId) => missing.add(orderId))
    }

    const mergedPdf = await PDFDocument.create()
    const addPages = async (pdfBytes: ArrayBuffer) => {
      const pdf = await PDFDocument.load(pdfBytes)
      const copiedPages = await mergedPdf.copyPages(pdf, pdf.getPageIndices())
      copiedPages.forEach((page) => {
        mergedPdf.addPage(page)
      })
    }

    // Consecutive orders with label data are rendered as one document, so
    // Flash's fallback PDFs can be slotted in between in the original order.
    let run: string[] = []
    const flushRun = async () => {
      if (!run.length) {
        return
      }
      this.setPopupMessage?.('กำลังสร้างใบปะหน้า')
      const blob = await renderLabels(run, labels)
      await addPages(await blob.arrayBuffer())
      this.successOrderIds.push(...run)
      run = []
    }

    let fallback = 0
    for (const orderId of orderIds) {
      if (labels[orderId]) {
        run.push(orderId)
        continue
      }
      if (!missing.has(orderId)) {
        continue
      }
      await flushRun()
      try {
        this.setPopupMessage?.(
          `กำลังดึงใบปะหน้าจาก Flash ${fallback + 1}/${missing.size}`,
        )
        await addPages(await this.fetchFlashPdf(orderId, size))
        this.successOrderIds.push(orderId)
        fallback++
      } catch (e) {
        console.error(e)
      }
    }
    await flushRun()

    if (this.successOrderIds.length > 0) {
      const mergedPdfFile = await mergedPdf.save()
      const blob = new Blob([mergedPdfFile], { type: 'application/pdf' })
      const url = window.URL.createObjectURL(blob)
      window.open(url, '_blank')
    } else {
      alert(`ไม่พบรายการพิมพ์ในระบบ`)
    }

    return {
      fallback,
      failed: orderIds.length - this.successOrderIds.length,
    }
  }

  public async bestexpress(orderIds: string[]): Promise<any> {
    const mergedPdf = await PDFDocument.create()
    let success = 0
    this.clearSuccessOrderIds()
    for (const orderId of orderIds) {
      try {
        const provider = this.provider.toLocaleLowerCase()
        const response = await axiosInstance.post(
          `/trackings/${provider}/print`,
          {
            provider: this.provider,
            orders: [orderId],
          },
          {
            headers: {
              'Content-Type': 'application/json',
            },
          },
        )

        const pdfStream = response.data?.pdf ?? null
        if (!pdfStream) {
          continue
        }
        const data = atob(pdfStream)
        const pdfBytes = new Uint8Array(data.length)
        for (let i = 0; i < data.length; i++) {
          pdfBytes[i] = data.charCodeAt(i)
        }

        const arrayBuffer = pdfBytes.buffer

        const pdf = await PDFDocument.load(arrayBuffer)
        const copiedPages = await mergedPdf.copyPages(pdf, pdf.getPageIndices())
        copiedPages.forEach((page) => {
          mergedPdf.addPage(page)
        })

        success++
        this.setPopupMessage?.('กำลังพิมพ์ ' + success + '/' + orderIds.length)
        this.successOrderIds.push(orderId)
      } catch (e) {
        console.error(e)
      }
    }

    if (success > 0) {
      const mergedPdfFile = await mergedPdf.save()
      const blob = new Blob([mergedPdfFile], { type: 'application/pdf' })
      const url = window.URL.createObjectURL(blob)
      window.open(url, '_blank')
    } else {
      alert(`ไม่พบรายการพิมพ์ในระบบ`)
    }
  }

  public async kerry(orders: any): Promise<any> {
    const mergedPdf = await PDFDocument.create()
    let success = 0
    this.clearSuccessOrderIds()
    for (const order of orders) {
      try {
        const queryString = this.objectToQueryString(order)
        const url = 'https://exportv1.systemtong.com/export/pdf/kerry'
        const pdfBytes = await fetch(url, {
          body: queryString,
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
        }).then((res) => res.arrayBuffer())

        if (!pdfBytes) {
          continue
        }

        const pdf = await PDFDocument.load(pdfBytes)
        const copiedPages = await mergedPdf.copyPages(pdf, pdf.getPageIndices())
        copiedPages.forEach((page) => {
          mergedPdf.addPage(page)
        })

        success++
        this.setPopupMessage?.('กำลังพิมพ์ ' + success + '/' + orders.length)
        this.successOrderIds.push(order.orderId)
      } catch (e) {
        console.error(e)
      }
    }

    if (success > 0) {
      const mergedPdfFile = await mergedPdf.save()
      const blob = new Blob([mergedPdfFile], { type: 'application/pdf' })
      const url = window.URL.createObjectURL(blob)
      window.open(url, '_blank')
    } else {
      alert(`ไม่พบรายการพิมพ์ในระบบ`)
    }
  }

  async setOrderIsPrinted(
    orderIds: string[],
    provider: string,
    ordersIdWithTrackings: any[],
  ): Promise<any> {
    try {
      await axiosInstance.put(`/order/setIsPrinted`, {
        orders: orderIds,
        provider,
        ordersIdWithTrackings,
      })
    } catch (e) {
      console.error(e)
    }
  }

  objectToQueryString = (obj: any, prefix = ''): string => {
    const queryString = Object.keys(obj)
      .map((key) => {
        const value = obj[key]
        const prefixedKey = prefix ? `${prefix}[${key}]` : key

        if (typeof value === 'object' && value !== null) {
          return this.objectToQueryString(value, prefixedKey)
        } else {
          return `${encodeURIComponent(prefixedKey)}=${encodeURIComponent(
            value,
          )}`
        }
      })
      .join('&')

    return queryString
  }
}
